import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';

import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';

import { styled, type CSSObject, type Theme } from '@mui/material/styles';
import ListItemIconMui from '@mui/material/ListItemIcon';

export const DRAWER_STATIC_WIDTH = 64;
export const DRAWER_MINI_WIDTH = 88;
export const DRAWER_EXPANDED_WIDTH = 260;

const openedMixin = (): CSSObject => ({
  width: DRAWER_EXPANDED_WIDTH,
  overflowX: 'hidden',
});

const closedMixin = (): CSSObject => ({
  width: DRAWER_MINI_WIDTH,
  overflowX: 'hidden',
});

const drawerPaperMixin = (theme: Theme): CSSObject => ({
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.primary.contrastText,
  borderRight: 'none',
});

export const DrawerStatic = styled(MuiDrawer)(({ theme }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  width: DRAWER_STATIC_WIDTH,

  '& .MuiDrawer-paper': {
    width: DRAWER_STATIC_WIDTH,
    ...drawerPaperMixin(theme),
  },
}));

export const DrawerExpanded = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',

    ...(open && {
      ...openedMixin(),

      '& .MuiDrawer-paper': {
        ...openedMixin(),
        ...drawerPaperMixin(theme),
      },
    }),
    ...(!open && {
      ...closedMixin(),

      '& .MuiDrawer-paper': {
        ...closedMixin(),
        ...drawerPaperMixin(theme),
      },
    }),
  }),
);

export const DrawerToggle = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.mixins.toolbar.minHeight,
  left: 0,
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[300]}`,
  boxShadow: theme.shadows[3],

  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[0],
  },
}));

export const DrawerToggleIcon = styled(ChevronLeftOutlinedIcon, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ theme, open }) => ({
  transform: theme.direction === 'rtl' ? `rotate(${open ? 180 : 0}deg)` : `rotate(${open ? 0 : 180}deg)`,
}));

export const DrawerHeader = styled('div', { shouldForwardProp: (prop) => prop !== 'open' })<{ open?: boolean }>(
  ({ theme, open }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: open ? 'start' : 'center',
    ...(open &&
      theme.labelOptions.showLogoInSidebar && {
        background: `url("${theme.labelOptions.whiteLogo.src}") center right 10px scroll no-repeat`,
        backgroundSize: '70px',
      }),
    // necessary for content to be below app bar
    minHeight: Number(theme.mixins.toolbar.minHeight) + 1, // +1 <AppBar> borderBottom
  }),
);

export const DrawerContent = styled('div')(() => ({
  overflowY: 'auto',
}));

export const DrawerFooter = styled('div')(() => ({
  marginTop: 'auto',
}));

export const ListItemIcon = styled(ListItemIconMui, {
  shouldForwardProp: (prop) => prop !== 'minWidth',
})<{ minWidth?: number | string }>(({ theme, minWidth }) => ({
  minWidth: minWidth || 0,
  marginRight: theme.spacing(1),
}));
